import {
    ApplicationModuleField,
    ApplicationModuleFieldLookup
} from "../api/applicationModuleFields";

/**
 * Describes which onboarding form owns a question.  "shared" is the
 * organization / Shared Configuration form.
 */
export enum OnboardingQuestionOwnership {
    Shared = "shared",
    Location = "location",
    Both = "both",
    Unmapped = "unmapped",
    Mixed = "mixed"
}

/**
 * Policies may intentionally place a question on both forms, but cannot
 * intentionally declare a question mixed.  A mixed result always means the
 * question's mapped variables disagree about where they live.
 */
export type ExplicitOnboardingQuestionOwnership =
    | OnboardingQuestionOwnership.Shared
    | OnboardingQuestionOwnership.Location
    | OnboardingQuestionOwnership.Both
    | OnboardingQuestionOwnership.Unmapped;

export interface OnboardingQuestionOwnershipDescriptor {
    id: string;
    associatedVariable?: string;
    associatedVariables?: string[];
    /**
     * Extra variables written by a composite question.  This is deliberately
     * supported separately from associatedVariables so callers can describe
     * variables produced by inline controls without changing a flow config.
     */
    variables?: string[];
    /** A fixed policy declared next to a question configuration. */
    ownership?: ExplicitOnboardingQuestionOwnership;
}

export interface OnboardingQuestionOwnershipPolicy {
    [questionId: string]: ExplicitOnboardingQuestionOwnership | undefined;
}

/**
 * The onboarding-progress properties which have historically cached Spark's
 * location-specific selection.  They are intentionally a fallback: current
 * Spark form data wins over stale progress details.
 */
export interface OnboardingOwnershipProgressCache {
    locationSpecificFieldKeys?: string[];
    locationSpecificQuestionIds?: string[];
    locationSpecificVariables?: string[];
    sparkLocationSpecificVariables?: string[];
}

export interface ResolveOnboardingQuestionOwnershipOptions {
    /** Overrides all other policy sources for one resolution call. */
    explicitOwnership?: ExplicitOnboardingQuestionOwnership;
    /** Fixed ownership rules keyed by onboarding question id. */
    ownershipPolicy?: OnboardingQuestionOwnershipPolicy;
    /** The Shared Configuration application's resolved module fields. */
    sharedModuleFieldLookup?: ApplicationModuleFieldLookup;
    /**
     * Fields in the actual location-specific form schema.  Do not pass a
     * flattened application lookup here unless it contains only that schema;
     * a normal location app can also expose inherited shared fields.
     */
    locationModuleFieldLookup?: ApplicationModuleFieldLookup;
    /** Associated variables selected into Spark's location-specific schema. */
    locationSchemaVariables?: string[];
    /** Persisted onboarding details used when live schema data is unavailable. */
    progressCache?: OnboardingOwnershipProgressCache;
}

export interface OnboardingVariableOwnershipResolution {
    associatedVariable: string;
    ownership: OnboardingQuestionOwnership.Shared | OnboardingQuestionOwnership.Location | OnboardingQuestionOwnership.Unmapped | OnboardingQuestionOwnership.Mixed;
    isInSharedLookup: boolean;
    isSharedSentinel: boolean;
    isInLocationModuleFieldLookup: boolean;
    isInLocationSchema: boolean;
    isInProgressCache: boolean;
}

export interface OnboardingQuestionOwnershipResolution {
    ownership: OnboardingQuestionOwnership;
    variables: string[];
    variableResolutions: OnboardingVariableOwnershipResolution[];
    explicitOwnership?: ExplicitOnboardingQuestionOwnership;
    isLocationSpecificInProgressCache: boolean;
}

const normalizeVariable = (value?: string): string => typeof value === "string" ? value.trim() : "";

const addVariablesToSet = (set: Set<string>, variables?: string[]): void => {
    (variables || []).forEach(variable => {
        const normalized = normalizeVariable(variable);
        if (normalized) {
            set.add(normalized);
        }
    });
};

/**
 * Returns every variable a question writes in stable order.  A composite
 * question must declare every saved variable here (or in `variables`) so it
 * can never be split between shared and location onboarding by accident.
 */
export const getDeclaredQuestionVariables = (question: OnboardingQuestionOwnershipDescriptor): string[] => {
    const variables = new Set<string>();
    const associatedVariable = normalizeVariable(question.associatedVariable);

    if (associatedVariable) {
        variables.add(associatedVariable);
    }

    addVariablesToSet(variables, question.associatedVariables);
    addVariablesToSet(variables, question.variables);

    return Array.from(variables);
};

const getLookupField = (
    lookup: ApplicationModuleFieldLookup | undefined,
    associatedVariable: string
): ApplicationModuleField | undefined => {
    if (!lookup) {
        return undefined;
    }

    if (lookup[associatedVariable]) {
        return lookup[associatedVariable];
    }

    return Object.keys(lookup)
        .map(key => lookup[key])
        .find(field => field?.associatedVariable === associatedVariable);
};

const getLookupVariables = (lookup?: ApplicationModuleFieldLookup): Set<string> => {
    const variables = new Set<string>();

    if (!lookup) {
        return variables;
    }

    Object.keys(lookup).forEach(key => {
        const normalizedKey = normalizeVariable(key);
        if (normalizedKey) {
            variables.add(normalizedKey);
        }

        const fieldVariable = normalizeVariable(lookup[key]?.associatedVariable);
        if (fieldVariable) {
            variables.add(fieldVariable);
        }
    });

    return variables;
};

/**
 * Reads the effective raw field value without converting sentinel values to an
 * empty string.  Spark uses the associated variable itself as the sentinel.
 */
export const getRawModuleFieldValue = (field?: ApplicationModuleField): string | undefined => {
    if (!field) {
        return undefined;
    }

    const values = [field.currentValue, field.value, field.defaultValue];
    const firstDefinedValue = values.find(value => value !== undefined && value !== null);

    return firstDefinedValue === undefined ? undefined : String(firstDefinedValue);
};

export const isLocationSpecificSharedValue = (
    field: ApplicationModuleField | undefined,
    associatedVariable: string
): boolean => getRawModuleFieldValue(field) === associatedVariable;

const getProgressLocationVariables = (cache?: OnboardingOwnershipProgressCache): Set<string> => {
    const variables = new Set<string>();
    addVariablesToSet(variables, cache?.locationSpecificVariables);
    addVariablesToSet(variables, cache?.sparkLocationSpecificVariables);
    return variables;
};

const isQuestionInProgressLocationCache = (
    questionId: string,
    cache?: OnboardingOwnershipProgressCache
): boolean => {
    const locationQuestionIds = new Set<string>();
    addVariablesToSet(locationQuestionIds, cache?.locationSpecificFieldKeys);
    addVariablesToSet(locationQuestionIds, cache?.locationSpecificQuestionIds);
    return locationQuestionIds.has(questionId);
};

const getExplicitOwnership = (
    question: OnboardingQuestionOwnershipDescriptor,
    options: ResolveOnboardingQuestionOwnershipOptions
): ExplicitOnboardingQuestionOwnership | undefined => {
    return options.explicitOwnership
        || options.ownershipPolicy?.[question.id]
        || question.ownership;
};

/**
 * Resolves ownership for an onboarding question from the same signals Spark
 * uses for location-specific fields.  Explicit policy always wins.  Without a
 * policy, every declared variable must be resolvable and agree; unresolved
 * variables produce `unmapped`, while conflicting shared/location variables
 * produce `mixed`.
 */
export const resolveOnboardingQuestionOwnership = (
    question: OnboardingQuestionOwnershipDescriptor,
    options: ResolveOnboardingQuestionOwnershipOptions = {}
): OnboardingQuestionOwnershipResolution => {
    const variables = getDeclaredQuestionVariables(question);
    const explicitOwnership = getExplicitOwnership(question, options);
    const locationSchemaVariables = new Set<string>();
    addVariablesToSet(locationSchemaVariables, options.locationSchemaVariables);
    const locationLookupVariables = getLookupVariables(options.locationModuleFieldLookup);
    const cachedLocationVariables = getProgressLocationVariables(options.progressCache);
    const isLocationSpecificInProgressCache = isQuestionInProgressLocationCache(question.id, options.progressCache);

    const variableResolutions = variables.map(associatedVariable => {
        const sharedField = getLookupField(options.sharedModuleFieldLookup, associatedVariable);
        const isInSharedLookup = !!sharedField;
        const isSharedSentinel = isLocationSpecificSharedValue(sharedField, associatedVariable);
        const isInLocationModuleFieldLookup = locationLookupVariables.has(associatedVariable);
        const isInLocationSchema = locationSchemaVariables.has(associatedVariable);
        const isInProgressCache = cachedLocationVariables.has(associatedVariable)
            || isLocationSpecificInProgressCache;

        const isLiveLocationSpecific = isSharedSentinel
            || isInLocationModuleFieldLookup
            || isInLocationSchema;
        const isLiveShared = isInSharedLookup && !isSharedSentinel;

        let ownership: OnboardingVariableOwnershipResolution["ownership"];

        if (isLiveLocationSpecific && isLiveShared) {
            ownership = OnboardingQuestionOwnership.Mixed;
        } else if (isLiveLocationSpecific) {
            ownership = OnboardingQuestionOwnership.Location;
        } else if (isLiveShared) {
            ownership = OnboardingQuestionOwnership.Shared;
        } else if (isInProgressCache) {
            ownership = OnboardingQuestionOwnership.Location;
        } else {
            ownership = OnboardingQuestionOwnership.Unmapped;
        }

        return {
            associatedVariable,
            ownership,
            isInSharedLookup,
            isSharedSentinel,
            isInLocationModuleFieldLookup,
            isInLocationSchema,
            isInProgressCache
        };
    });

    if (explicitOwnership) {
        return {
            ownership: explicitOwnership,
            variables,
            variableResolutions,
            explicitOwnership,
            isLocationSpecificInProgressCache
        };
    }

    if (variables.length === 0) {
        return {
            ownership: isLocationSpecificInProgressCache
                ? OnboardingQuestionOwnership.Location
                : OnboardingQuestionOwnership.Unmapped,
            variables,
            variableResolutions,
            isLocationSpecificInProgressCache
        };
    }

    const ownerships = new Set(variableResolutions.map(resolution => resolution.ownership));
    const hasMixedOwnership = ownerships.has(OnboardingQuestionOwnership.Mixed)
        || (ownerships.has(OnboardingQuestionOwnership.Shared)
            && ownerships.has(OnboardingQuestionOwnership.Location));

    if (hasMixedOwnership) {
        return {
            ownership: OnboardingQuestionOwnership.Mixed,
            variables,
            variableResolutions,
            isLocationSpecificInProgressCache
        };
    }

    // Do not route a composite question until every variable has a home.
    if (ownerships.has(OnboardingQuestionOwnership.Unmapped)) {
        return {
            ownership: OnboardingQuestionOwnership.Unmapped,
            variables,
            variableResolutions,
            isLocationSpecificInProgressCache
        };
    }

    return {
        ownership: ownerships.has(OnboardingQuestionOwnership.Location)
            ? OnboardingQuestionOwnership.Location
            : OnboardingQuestionOwnership.Shared,
        variables,
        variableResolutions,
        isLocationSpecificInProgressCache
    };
};
